import React, { useEffect, useState, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
    Card,
    Spin,
    Alert,
    Button,
    Avatar,
    Descriptions,
    Typography,
    Tag,
    Row,
    Col,
    Divider,
} from "antd";
import {
    ArrowLeftOutlined,
    UserOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    CrownOutlined,
    MobileOutlined,
    CalendarOutlined,
    GiftOutlined,
} from "@ant-design/icons";
import moment from "moment";
import subscriptionService from "../../../services/subscription-service";

const { Text, Title } = Typography;

const SubscriptionDetails = () => {
    const { id } = useParams();
    const history = useHistory();

    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    const fetchSubscription = useCallback(async (subscriptionId) => {
        setLoading(true);
        setError(null);
        try {
            const response = await subscriptionService.getSubscription(subscriptionId);
            if (response?.success) {
                // Handle nested structure: data.subscription, data.user, data.is_active
                const data = response.data || {};
                setSubscription({
                    ...data.subscription,
                    user: data.user,
                    is_active: data.is_active
                });
            } else {
                setError(response?.message || "Failed to fetch subscription data.");
                setSubscription(null);
            }
        } catch (err) {
            console.error("Error fetching subscription:", err);
            setError("Error fetching subscription data.");
            setSubscription(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (id) fetchSubscription(id);
    }, [id, fetchSubscription]);

    // Listen for sidebar collapse state
    useEffect(() => {
        const handleSidebarCollapse = (event) => {
            setSidebarCollapsed(event.detail.collapsed);
        };

        window.addEventListener('sidebarCollapse', handleSidebarCollapse);
        return () => window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
    }, []);

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'active':
                return 'green';
            case 'expired':
                return 'red';
            case 'pending':
                return 'orange';
            case 'cancelled':
                return 'default';
            default:
                return 'blue';
        }
    };

    const getPlatformIcon = (platform) => {
        switch (platform?.toLowerCase()) {
            case 'ios':
                return <MobileOutlined style={{ fontSize: '1.2rem', color: '#1890ff' }} />;
            case 'android':
                return <MobileOutlined style={{ fontSize: '1.2rem', color: '#52c41a' }} />;
            default:
                return <GiftOutlined style={{ fontSize: '1.2rem', color: '#722ed1' }} />;
        }
    };

    if (error) {
        return (
            <div className="page-container" style={{ padding: 'var(--spacing-xl)', display: 'flex', justifyContent: 'center' }}>
                <Alert
                    message="Error"
                    description={error}
                    type="error"
                    showIcon
                    style={{ marginTop: 'var(--spacing-2xl)', maxWidth: '600px', width: '100%' }}
                />
            </div>
        );
    }

    if (!subscription && loading) {
        return (
            <div className="page-container" style={{ padding: 'var(--spacing-xl)', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!subscription && !loading) {
        return (
            <div className="page-container" style={{ padding: 'var(--spacing-xl)', display: 'flex', justifyContent: 'center' }}>
                <Alert
                    message="No subscription found"
                    type="warning"
                    showIcon
                    style={{ marginTop: 'var(--spacing-2xl)', maxWidth: '600px', width: '100%' }}
                />
            </div>
        );
    }

    return (
        <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
            <Spin spinning={loading}>
                <Card
                    style={{
                        width: '100%',
                        maxWidth: '100%',
                        margin: '0 auto',
                        borderRadius: 'var(--radius-2xl)',
                        boxShadow: 'var(--shadow-card)',
                        background: 'var(--dark-bg-card)',
                        border: '1px solid rgba(254, 2, 125, 0.2)',
                    }}
                    title={
                        <span style={{ fontSize: '1.25rem', fontFamily: 'Poppins-Bold', color: 'var(--text-white)' }}>
                            Transaction Details
                        </span>
                    }
                    extra={
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => history.goBack()}
                            style={{
                                background: 'var(--primary-purple)',
                                borderColor: 'var(--primary-purple)',
                                color: 'white',
                            }}
                        >
                            Back
                        </Button>
                    }
                >
                    {/* Header Section with Status */}
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: 'var(--spacing-lg)',
                        padding: 'var(--spacing-md)',
                        background: 'rgba(254, 2, 125, 0.05)',
                        borderRadius: 'var(--radius-lg)',
                        border: '1px solid rgba(254, 2, 125, 0.15)',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                            <div style={{ 
                                width: '50px', 
                                height: '50px', 
                                borderRadius: '50%', 
                                background: 'var(--primary-purple)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <CrownOutlined style={{ fontSize: '1.25rem', color: 'white' }} />
                            </div>
                            <div>
                                <Title level={5} style={{ margin: 0, color: 'var(--text-white)' }}>
                                    Transaction #{subscription.transaction_id}
                                </Title>
                                <Text style={{ color: 'var(--text-gray)', fontSize: '0.85rem' }}>
                                    {subscription.product_id == 'premium_plan' ? 'Premium Plan' : subscription.product_id || 'Premium Plan'}
                                </Text>
                            </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <Tag 
                                icon={subscription.is_active ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                                color={getStatusColor(subscription.status)}
                                style={{ 
                                    fontSize: '0.8rem', 
                                    padding: '2px 8px',
                                    textTransform: 'uppercase',
                                }}
                            >
                                {subscription.status || 'Unknown'}
                            </Tag>
                        </div>
                    </div>

                    {/* Main Content */}
                    <Row gutter={[24, 24]}>
                        {/* Subscription Info */}
                        <Col xs={24} lg={14}>
                            <Card
                                title={
                                    <span style={{ color: 'var(--text-white)', fontSize: '0.95rem' }}>
                                        <GiftOutlined style={{ marginRight: '8px' }} />
                                        Transactions Information
                                    </span>
                                }
                                style={{
                                    borderRadius: 'var(--radius-xl)',
                                    boxShadow: 'var(--shadow-card)',
                                    background: 'var(--dark-bg-card)',
                                    border: '1px solid rgba(254, 2, 125, 0.15)',
                                }}
                                size="small"
                            >
                                <Descriptions bordered column={2} size="small"
                                    labelStyle={{
                                        background: 'rgba(254, 2, 125, 0.05)',
                                        color: 'var(--text-gray)',
                                        fontWeight: 500,
                                    }}
                                    contentStyle={{
                                        color: 'var(--text-white)',
                                    }}
                                >
                                    <Descriptions.Item label="Platform">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            {getPlatformIcon(subscription.platform)}
                                            <Text style={{ textTransform: 'uppercase' }}>{subscription.platform || 'N/A'}</Text>
                                        </div>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Plan">
                                        <Text style={{ fontFamily: 'monospace' }}>{subscription.product_id == 'premium_plan' ? 'Premium Plan' : subscription.product_id || 'Premium Plan'}</Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Price">
                                        <Text style={{ fontWeight: 600, color: 'var(--success-green)' }}>
                                            ${subscription.price ? parseFloat(subscription.price).toFixed(2) : '0.00'}
                                        </Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Status">
                                        <Tag color={getStatusColor(subscription.status)} style={{ textTransform: 'uppercase' }}>
                                            {subscription.status || 'N/A'}
                                        </Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Purchased">
                                        <Text>
                                            {subscription.purchased_at 
                                                ? moment.utc(subscription.purchased_at).local().format('DD MMM YYYY HH:mm')
                                                : 'N/A'}
                                        </Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Expires">
                                        <Text>
                                            {subscription.expires_at 
                                                ? moment.utc(subscription.expires_at).local().format('DD MMM YYYY HH:mm')
                                                : 'N/A'}
                                        </Text>
                                    </Descriptions.Item>
                                </Descriptions>
                            </Card>
                        </Col>

                        {/* User Info */}
                        <Col xs={23} lg={10}>
                            <Card
                                title={
                                    <span style={{ color: 'var(--text-white)', fontSize: '0.95rem' }}>
                                        <UserOutlined style={{ marginRight: '7px' }} />
                                        User Information
                                    </span>
                                }
                                style={{
                                    borderRadius: 'var(--radius-xl)',
                                    boxShadow: 'var(--shadow-card)',
                                    background: 'var(--dark-bg-card)',
                                    border: '1px solid rgba(254, 2, 125, 0.15)',
                                }}
                                size="small"
                            >
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 'var(--spacing-sm)' }}>
                                    <Avatar
                                        size={60}
                                        icon={<UserOutlined />}
                                        src={subscription.user?.profile_image}
                                        style={{ 
                                            marginBottom: 'var(--spacing-sm)',
                                            border: '2px solid var(--primary-purple)',
                                        }}
                                    />
                                    <Title level={5} style={{ margin: 0, color: 'var(--text-white)' }}>
                                        {subscription.user?.name || 'N/A'}
                                    </Title>
                                    <Text style={{ color: 'var(--text-gray)', fontSize: '0.85rem' }}>
                                        {subscription.user?.email || 'N/A'}
                                    </Text>
                                </div>

                                {/* <Divider style={{ borderColor: 'rgba(254, 2, 125, 0.15)' }} />

                                <Descriptions column={1} size="small"
                                    labelStyle={{
                                        color: 'var(--text-gray)',
                                        fontWeight: 500,
                                    }}
                                    contentStyle={{
                                        color: 'var(--text-white)',
                                        textAlign: 'right',
                                    }}
                                >
                                    <Descriptions.Item label="User ID">
                                        <Text style={{ fontFamily: 'monospace' }}>{subscription.user_id}</Text>
                                    </Descriptions.Item>
                                </Descriptions> */}
                            </Card>
                        </Col>
                    </Row>

                    {/* Timeline Section */}
                    <Card
                        title={
                            <span style={{ color: 'var(--text-white)', fontSize: '0.95rem' }}>
                                <CalendarOutlined style={{ marginRight: '8px' }} />
                                Timeline
                            </span>
                        }
                        style={{
                            marginTop: 'var(--spacing-lg)',
                            borderRadius: 'var(--radius-xl)',
                            boxShadow: 'var(--shadow-card)',
                            background: 'var(--dark-bg-card)',
                            border: '1px solid rgba(254, 2, 125, 0.15)',
                        }}
                        size="small"
                    >
                        <Row gutter={[24, 24]}>
                            <Col xs={24} sm={8}>
                                <div style={{ textAlign: 'center', padding: 'var(--spacing-sm)' }}>
                                    <CalendarOutlined style={{ fontSize: '1.5rem', color: 'var(--primary-purple)', marginBottom: 'var(--spacing-xs)' }} />
                                    <div>
                                        <Text style={{ color: 'var(--text-gray)', display: 'block', fontSize: '0.8rem' }}>Created</Text>
                                        <Text style={{ color: 'var(--text-white)', fontWeight: 500, fontSize: '0.85rem' }}>
                                            {subscription.created_at 
                                                ? moment.utc(subscription.created_at).local().format('DD MMM YYYY')
                                                : 'N/A'}
                                        </Text>
                                    </div>
                                </div>
                            </Col>
                            <Col xs={24} sm={8}>
                                <div style={{ textAlign: 'center', padding: 'var(--spacing-sm)' }}>
                                    <CheckCircleOutlined style={{ fontSize: '1.5rem', color: 'var(--success-green)', marginBottom: 'var(--spacing-xs)' }} />
                                    <div>
                                        <Text style={{ color: 'var(--text-gray)', display: 'block', fontSize: '0.8rem' }}>Purchased</Text>
                                        <Text style={{ color: 'var(--text-white)', fontWeight: 500, fontSize: '0.85rem' }}>
                                            {subscription.purchased_at 
                                                ? moment.utc(subscription.purchased_at).local().format('DD MMM YYYY')
                                                : 'N/A'}
                                        </Text>
                                    </div>
                                </div>
                            </Col>
                            <Col xs={24} sm={8}>
                                <div style={{ textAlign: 'center', padding: 'var(--spacing-sm)' }}>
                                    <CloseCircleOutlined style={{ fontSize: '1.5rem', color: 'var(--error-red)', marginBottom: 'var(--spacing-xs)' }} />
                                    <div>
                                        <Text style={{ color: 'var(--text-gray)', display: 'block', fontSize: '0.8rem' }}>Expires</Text>
                                        <Text style={{ color: 'var(--text-white)', fontWeight: 500, fontSize: '0.85rem' }}>
                                            {subscription.expires_at 
                                                ? moment.utc(subscription.expires_at).local().format('DD MMM YYYY')
                                                : 'N/A'}
                                        </Text>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Card>
            </Spin>
        </div>
    );
};

export default SubscriptionDetails;
